import { memo, useMemo } from 'react';
import { Skeleton, Stack } from '@mui/material';
import Table, { ColumnOption } from 'domains/core/components/Table';
import { AccessCentreUser } from 'domains/accessCentre/types';
import { formatTimestamp } from 'domains/accessCentre/util';
import UsersTableOptionsCell from './UsersTableOptionsCell';

type Props = {
    users: AccessCentreUser[];
    isLoading?: boolean;
    isRefetching?: boolean;
    refetch?: () => void;
};

const UsersTable = ({ users, isLoading, isRefetching, refetch }: Props) => {
    const data: object[] = useMemo(() => [...users], [users]);

    const columns: ColumnOption[] = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                align: 'left',
                disableSortBy: true,
                Cell: (row: any) => row.cell.value || '—',
            },
            { Header: 'Email', accessor: 'email', align: 'left', disableSortBy: true },
            {
                Header: 'Role',
                accessor: 'role_name',
                align: 'left',
                disableSortBy: true,
            },
            {
                Header: 'Updated at',
                accessor: 'last_modified_timestamp',
                disableSortBy: true,
                Cell: (row: any) => formatTimestamp(row.row.original.last_modified_timestamp),
            },
            {
                Header: 'Date created',
                accessor: 'created_at',
                disableSortBy: true,
                Cell: (row: any) => formatTimestamp(row.row.original.created_at),
            },
            {
                Header: '',
                accessor: 'id',
                align: 'right',
                disableSortBy: true,
                Cell: (val: any) => <UsersTableOptionsCell user={val.row.original} />,
            },
        ],
        []
    );

    if (isLoading) {
        return (
            <Stack
                spacing={1}
                sx={{ pt: 2 }}
                data-testid="access-center-users-table-loading"
                role="progressbar"
                aria-label="Loading users"
            >
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={48} />
                ))}
            </Stack>
        );
    }

    return (
        <Stack data-testid="access-center-users-table" role="table" aria-label="Users table">
            <Table columns={columns} data={data} isRefetching={isRefetching} refetch={refetch} stickyHeader />
        </Stack>
    );
};

export default memo(UsersTable);
